import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { IconClose, IconChevronRight, IconChevronLeft, IconSearch } from "./icons";
import styles from "./MobileNavDrawer.module.css";

// Modern left-side mobile nav drawer.
//
// Behaviour mirrors the iOS pattern: tap a top-level item with children
// and the panel slides one level deeper, showing a back arrow and the
// parent label as a header. Items without children navigate directly.
export default function MobileNavDrawer({
  open,
  onClose,
  navItems = [],
  utilityItems = [],
  onSearch,
}) {
  const [active, setActive] = useState(null); // { label, children }
  const location = useLocation();

  // Reset to top level when the drawer closes or the route changes.
  useEffect(() => { if (!open) setActive(null); }, [open]);
  useEffect(() => { onClose?.(); }, [location.pathname]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <>
      <div
        className={[styles.scrim, open ? styles.scrimOpen : ""].join(" ")}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside
        className={[styles.panel, open ? styles.panelOpen : ""].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        aria-hidden={!open}
      >
        <header className={styles.head}>
          {active ? (
            <button type="button" className={styles.back} onClick={() => setActive(null)} aria-label="Back">
              <IconChevronLeft />
              <span>Back</span>
            </button>
          ) : (
            <span className={styles.brand}>MYSTERY RANCH</span>
          )}
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close menu">
            <IconClose />
          </button>
        </header>

        {!active && onSearch && (
          <button
            type="button"
            className={styles.search}
            onClick={() => { onClose?.(); onSearch(); }}
          >
            <IconSearch size={18} />
            <span>Search</span>
          </button>
        )}

        <div className={styles.panes}>
          {/* Pane 1 — top level */}
          <nav
            className={[styles.pane, active ? styles.paneOut : ""].join(" ")}
            aria-hidden={!!active}
          >
            <div className={styles.sectionLabel}>Shop</div>
            <ul className={styles.list}>
              {navItems.map((item) => (
                <li key={item.label}>
                  {item.children?.length ? (
                    <button
                      type="button"
                      className={styles.row}
                      onClick={() => setActive(item)}
                    >
                      <span className={styles.rowLabel}>{item.label}</span>
                      <IconChevronRight size={18} />
                    </button>
                  ) : (
                    <Link to={item.path} className={styles.row} onClick={onClose}>
                      <span className={styles.rowLabel}>{item.label}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {utilityItems.length > 0 && (
              <>
                <div className={styles.sectionLabel}>Account</div>
                <ul className={styles.list}>
                  {utilityItems.map((u) => (
                    <li key={u.label}>
                      {u.path ? (
                        <Link to={u.path} className={styles.utilityRow} onClick={onClose}>
                          {u.label}
                        </Link>
                      ) : u.href ? (
                        <a href={u.href} className={styles.utilityRow} rel="noopener" onClick={onClose}>
                          {u.label}
                        </a>
                      ) : u.onClick ? (
                        <button type="button" className={styles.utilityRow} onClick={() => { onClose?.(); u.onClick(); }}>
                          {u.label}
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </nav>

          {/* Pane 2 — submenu */}
          <nav
            className={[styles.pane, styles.paneSub, active ? styles.paneIn : ""].join(" ")}
            aria-hidden={!active}
          >
            {active && (
              <>
                <div className={styles.subTitle}>{active.label}</div>
                <ul className={styles.list}>
                  {active.children.map((c) => (
                    <li key={c.label}>
                      <Link to={c.path} className={styles.row} onClick={onClose}>
                        <span className={styles.rowLabel}>{c.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </nav>
        </div>
      </aside>
    </>
  );
}
